import { inject, Injectable } from '@angular/core';
import { AbstractSandbox } from 'app/core/sandboxes/abstract.sandbox';
import { AuthorizationMatrix } from 'app/core/store/session/authorization/authorization.interface';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { Definitions } from '../store/definitions/definitions.interface';
import { map } from 'rxjs/operators';
import { checkAuthorization, util } from '../util/util';
import { BusyAction, BusyDoneAction } from '../store/ui/ui.actions';
import { InvoiceGetPdfRequest, InvoiceQueryFilter, InvoiceQueryRequest, InvoiceQueryResponse } from "app/core/interfaces/invoice.interface";
import { InvoicesRestService } from "app/core/services/rest/invoices-rest.service";

@Injectable({ providedIn: 'root' })
export class InvoiceSandbox extends AbstractSandbox {
  busy$ = this.store.select(state => state.ui.busy);
  invoiceTypes$ = this.store.select('definitions').pipe(map((definitions: Definitions) => definitions.invoices.invoiceTypes));
  authorization$ = this.store.pipe(map(state => state.session.authorization.data));
  isSuperUser$ = this.store.select(state => state.session.userContext.isSuperUser);
  soldTos$ = combineLatest([
    this.isSuperUser$,
    this.store.select(state => state.session.customerPermissions.data.invoices),
    this.store.select(state => state.session.selectedCustomer.data),
  ]).pipe(
    map(([isSuperUser, customers, selectedCustomer]) => {
      if (isSuperUser) {
        return customers;
      } else {
        return [selectedCustomer];
      }
    }));

  private authorization: AuthorizationMatrix;

  private invoicesRestService = inject(InvoicesRestService);

  constructor() {
    super();
    this.authorization$.subscribe(authorization => (this.authorization = authorization));
  }

  getInvoices(request: InvoiceQueryRequest): Observable<InvoiceQueryResponse> {
    checkAuthorization(this.authorization?.INVOICES.QUERY, 'Access to invoices is not allowed');
    return this.invoicesRestService.getInvoices(request);
  }

  async downloadInvoice(request: InvoiceGetPdfRequest): Promise<void> {
    if (await firstValueFrom(this.busy$)) {
      return;
    }
    checkAuthorization(this.authorization?.INVOICES.GET_INVOICE_DOCUMENT, 'Downloading an invoice is not allowed');
    this.store.dispatch(new BusyAction('DOWNLOAD_INVOICE'));
    try {
      const blob = await firstValueFrom(this.invoicesRestService.getInvoiceAsDocument(request));
      util.openBlob(blob, `invoice-${request.invoiceNumber}.pdf`);
    } catch (error) {
      this.notificationService.showToastr({
        message: 'Unable to open PDF',
        type: 'error'
      })
    } finally {
      this.store.dispatch(new BusyDoneAction('DOWNLOAD_INVOICE'));
    }
  }

  async downloadList(request: InvoiceQueryFilter): Promise<void> {
    if (await firstValueFrom(this.busy$)) {
      return;
    }
    checkAuthorization(this.authorization?.INVOICES.LIST_INVOICES_AS_DOCUMENT, 'Access to invoices is not allowed');
    this.store.dispatch(new BusyAction('DOWNLOAD_INVOICE_LIST'));
    try {
    const blob = await firstValueFrom(this.invoicesRestService.getInvoicesAsExcel(request));
    util.openBlob(blob, 'invoices.xlsx');
  } catch (error) {
    this.notificationService.showToastr({
      message: 'Unable to open excel list',
      type: 'error',
    })
  } finally {
      this.store.dispatch(new BusyDoneAction('DOWNLOAD_INVOICE_LIST'));
    }
  }
}
